// Tag card — list-item summary used in the DV main page and ReportView.
// Extracted from /app/frontend/src/pages/DiamondVerification.jsx.

import React from 'react';
import { Trash2 } from 'lucide-react';
import { LAB_COLORS } from './constants';
import { SourceBadge } from './common';

export const TagCard = ({ tag, onClick, onDelete, isPrivileged }) => {
  const isDraft = tag.status === 'draft';
  const showDelete = onDelete && (isDraft || isPrivileged);
  const labs    = (tag.lab_proposals || []).map(p => p.lab);
  const entries = tag.actual?.entries || [];
  const totalCts = tag.actual?.total_weight_cts;
  const totalPcs = tag.actual?.diamond_count;

  return (
    <div className="mb-3">
      <button
        onClick={onClick}
        data-testid={`tag-card-${tag.tag_number}`}
        className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-left active:bg-gray-50 transition-all"
      >
        {/* Top row */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold text-gray-900 font-mono">#{tag.tag_number}</span>
              {(() => {
                if (isDraft) {
                  return <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700">{`Draft · Step ${tag.current_step}`}</span>;
                }
                if (tag.lab_status === 'in_lab') {
                  if (tag.sell_mode === 'tds_interim') {
                    return <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-sky-100 text-sky-700" data-testid={`tag-status-tds-interim-${tag.tag_number}`}>Sellable at TDS · Lab pending</span>;
                  }
                  return <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-orange-100 text-orange-700" data-testid={`tag-status-in-lab-${tag.tag_number}`}>In Lab</span>;
                }
                return <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-700">✓ Complete</span>;
              })()}
              {tag.actual?.data_source && <SourceBadge source={tag.actual.data_source} />}
            </div>
            {tag.jewellery_ref && (
              <p className="text-sm text-gray-500 mt-0.5">{tag.jewellery_ref}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <p className="text-[10px] text-gray-400">
              {new Date(tag.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
            </p>
            {showDelete && (
              <button
                onClick={e => { e.stopPropagation(); onDelete(tag); }}
                data-testid={`delete-tag-${tag.tag_number}`}
                className="p-1.5 rounded-lg bg-red-50 text-red-400 active:bg-red-100 border border-red-100"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Lab chips */}
        {labs.length > 0 && (
          <div className="flex gap-1.5 mb-2 flex-wrap">
            {labs.map(l => (
              <span key={l} className={`text-[10px] px-2.5 py-0.5 rounded-full text-white font-bold ${LAB_COLORS[l]}`}>{l}</span>
            ))}
          </div>
        )}

        {/* Stats bar */}
        {!isDraft && (entries.length > 0 || totalCts) && (
          <div className="mt-2 pt-2.5 border-t border-gray-100 flex items-center gap-3 text-xs">
            {entries.length > 0 && (
              <div className="flex items-center gap-1 text-gray-500">
                <span className="font-bold text-gray-800">{entries.length}</span>
                <span>{entries.length === 1 ? 'entry' : 'entries'}</span>
              </div>
            )}
            {totalCts != null && (
              <div className="flex items-center gap-1 text-gray-500">
                <span className="font-bold text-emerald-700">{parseFloat(totalCts).toFixed(3)}</span>
                <span>cts</span>
              </div>
            )}
            {totalPcs != null && (
              <div className="flex items-center gap-1 text-gray-500">
                <span className="font-bold text-emerald-700">{totalPcs}</span>
                <span>pcs</span>
              </div>
            )}
            {tag.actual?.gross_weight && (
              <div className="flex items-center gap-1 text-gray-500 ml-auto">
                <span className="font-bold text-gray-700">{tag.actual.gross_weight}g</span>
                <span>gross</span>
              </div>
            )}
          </div>
        )}
      </button>
    </div>
  );
};
